import {
    CmsPriceStyles,
    CmsPriceStylesDetail,
    PriceStyles,
    PricesType,
    ProductMapPrices,
    ProductPricesDetail,
    SalesPeriod,
    VipSalesDateRange,
} from '../models/product-prices.model';

import { getTimeZoneFromCountry } from '@core/application/use-cases/dates/get-timezone-from.country.use-case';
import { DateRange } from '@core/domain/models/dates.models';
import { checkIsVipDate } from '@features/user-events';
import { DuttiDate, DuttiDateFormat } from '@frontend/common/duttidate';

export const dicc = {
    [SalesPeriod.DEFAULT]: ({
        price,
        oldPrice,
        baseColor,
        eventColor,
        isBlackFriday,
        isVipRange,
        configTriplePriceRemark,
        hasPromotion,
    }) => {
        if (price.type === PricesType.PRICE && !!oldPrice.value && isBlackFriday) {
            return {
                backgroundColor: eventColor.background_color,
                textColor: eventColor.text_color,
            };
        }

        if (price.type === PricesType.PRICE && !!oldPrice.value) {
            return {
                backgroundColor: baseColor.background_color,
                textColor: baseColor.text_color,
            };
        }

        // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
        if (configTriplePriceRemark && hasPromotion && !!!oldPrice.value) {
            if (isVipRange || isBlackFriday) {
                return {
                    backgroundColor: eventColor.background_color,
                    textColor: eventColor.text_color,
                };
            }
            return {
                backgroundColor: baseColor.background_color,
                textColor: baseColor.text_color,
            };
        }
    },
    [SalesPeriod.PREWARM]: ({
        price, oldPrice, baseColor, eventColor,
    }) => {
        if (price.type === PricesType.PRICE && !!oldPrice.value) {
            return {
                backgroundColor: baseColor.background_color,
                textColor: baseColor.text_color,
            };
        }

        if (price.type === PricesType.FUTUREPRICE && !!oldPrice.value) {
            return {
                backgroundColor: eventColor.background_color,
                textColor: eventColor.text_color,
            };
        }
    },

    [SalesPeriod.VEA]: ({
        price, oldPrice, baseColor, eventColor, isVip, enableRebajasSinCorte,
    }) => {
        if (price.type === PricesType.PRICE && !!oldPrice.value) {
            if (enableRebajasSinCorte && isVip) {
                return {
                    backgroundColor: eventColor.background_color,
                    textColor: eventColor.text_color,
                };
            }

            return {
                backgroundColor: baseColor.background_color,
                textColor: baseColor.text_color,
            };
        }

        // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar la comprobación de la configuración de CMS y eliminar el codigo legacy)
        if (enableRebajasSinCorte) {
            if (price.type === PricesType.FUTUREPRICE && !!oldPrice.value) {
                return {
                    backgroundColor: eventColor.background_color,
                    textColor: eventColor.text_color,
                };
            }
        } else if ((price.type === PricesType.FUTUREPRICE || price.type === PricesType.VIPPRICE) && !!oldPrice.value) {
            return {
                backgroundColor: eventColor.background_color,
                textColor: eventColor.text_color,
            };
        }
    },
    [SalesPeriod.SALES]: ({
        price, oldPrice, eventColor, isBlackFriday, configTriplePriceRemark, hasPromotion, enableRebajasSinCorte,
    }) => {
        // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar la comprobación de la configuración de CMS y eliminar el codigo legacy)
        if (enableRebajasSinCorte) {
            if (price.type === PricesType.PRICE && !!oldPrice.value && isBlackFriday) {
                return {
                    backgroundColor: eventColor.background_color,
                    textColor: eventColor.text_color,
                };
            }
        // eslint-disable-next-line max-len
        } else if ((price.type === PricesType.PRICE || price.type === PricesType.VIPPRICE) && !!oldPrice.value && isBlackFriday) {
            return {
                backgroundColor: eventColor.background_color,
                textColor: eventColor.text_color,
            };
        }

        // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
        if (configTriplePriceRemark && hasPromotion && !!!oldPrice.value) {
            return {
                backgroundColor: eventColor.background_color,
                textColor: eventColor.text_color,
            };
        }
    },
};

export function getCurrentDateInTimeZone(date: DuttiDate, timezone: string): DuttiDate {
    const dateString = date.format(DuttiDateFormat.DATE_HOUR_MINUTE_SECOND);
    return new DuttiDate(
        dateString,
        DuttiDateFormat.DATE_HOUR_MINUTE_SECOND,
        timezone,
    );
}

export function isCurrentDateInBlackFriday(currentDate: DuttiDate, dateRange: DateRange, timezone: string): boolean {
    return currentDate.isBetween(
        new DuttiDate(dateRange.from, DuttiDateFormat.DATE_HOUR_MINUTE_SECOND, timezone),
        new DuttiDate(dateRange.to, DuttiDateFormat.DATE_HOUR_MINUTE_SECOND, timezone),
    );
}

export function calculatePriceStyles(
    period: string,
    price: ProductPricesDetail,
    oldPrice: ProductPricesDetail,
    isBlackFriday: boolean,
    isVipRange: boolean,
    baseColor: CmsPriceStylesDetail,
    eventColor: CmsPriceStylesDetail,
    configTriplePriceRemark: boolean,
    hasPromotion: boolean,
    isVip: boolean,
    enableRebajasSinCorte: boolean,
): PriceStyles {
    const fn = dicc[period];

    if (!fn) {
        return { backgroundColor: '', textColor: '' };
    }

    return (
        fn({
            price,
            oldPrice,
            baseColor,
            eventColor,
            isBlackFriday,
            isVipRange,
            configTriplePriceRemark,
            hasPromotion,
            isVip,
            enableRebajasSinCorte,
        }) ?? { backgroundColor: '', textColor: '' }
    );
}

export function getPriceBackgroundAndColorUseCase(
    mapPrices: ProductMapPrices,
    countryCode: string,
    currentDate: DuttiDate,
    { base, event }: CmsPriceStyles,
    period: string,
    blackFridayDateRange: DateRange,
    vipFridayDateRange: VipSalesDateRange,
    configTriplePriceRemark: boolean,
    hasPromotion: boolean,
    isVip: boolean,
    enableRebajasSinCorte: boolean,
) {
    const price = mapPrices.price;
    const oldPrice = mapPrices.oldPrice;
    const timezone = getTimeZoneFromCountry(countryCode);
    const currentDateZ = getCurrentDateInTimeZone(currentDate, timezone);
    const isBlackFriday = isCurrentDateInBlackFriday(currentDateZ, blackFridayDateRange, timezone);
    const isVipRange = checkIsVipDate(vipFridayDateRange, timezone, currentDateZ);

    return calculatePriceStyles(
        period,
        price,
        oldPrice,
        isBlackFriday,
        isVipRange,
        base,
        event,
        configTriplePriceRemark,
        hasPromotion,
        isVip,
        enableRebajasSinCorte,
    );
}
